// @flow

import * as React from 'react';

import {useAccount, useApi} from 'docs/data';
import {
  defaultSelection,
  ConnectRequiredInfoSelector,
} from './ConnectRequiredInfoSelector';
import {ConnectRequiredInfoTable} from './ConnectRequiredInfoTable';

const {useState} = React;

type ConnectRequiredInfoProps = {|
  capabilityTypesData: string,
  countryMapData: string,
  countryTosMapData: string,
  entityTypeStructureMapData: string,
|};

type TosType = 'full' | 'recipient';

export type ConnectRequiredInfoSelection = {|
  capabilityTypeSet: Set<string>,
  country: string,
  tosType: TosType,
  entityType: string,
  businessStructure: ?string,
|};

const clearDynamicDocs = () => {
  document.querySelectorAll('.ConnectRequiredInfoDoc').forEach((e) => {
    e.style.display = '';
  });
};

const showDynamicDocs = (
  country: string,
  entityType: string,
  sections: Array<string>,
  capabilityTypes: Array<string>,
) => {
  const isSuperset = (set, subset) => {
    for (const elem of subset) {
      if (!set.has(elem)) {
        return false;
      }
    }
    return true;
  };

  const euCountries = [
    'AT',
    'BE',
    'BG',
    'CY',
    'CZ',
    'DE',
    'DK',
    'EE',
    'ES',
    'FI',
    'FR',
    'GB',
    'GI',
    'GR',
    'HR',
    'HU',
    'IE',
    'IT',
    'LT',
    'LU',
    'LV',
    'MT',
    'NL',
    'PL',
    'PT',
    'RO',
    'SE',
    'SI',
    'SK',
  ];

  const targetClassNameSet = new Set([
    `reqinfo--${country}`,
    `reqinfo--entity_${entityType}`, // prefixed with entity_ to prevent collisions
    ...sections.map((e) => `reqinfo--${e}`),
    ...capabilityTypes.map((e) => `reqinfo--${e}`),
  ]);

  // helper expansions
  if (entityType !== 'individual') {
    targetClassNameSet.add('reqinfo--entity_company_type');
  }
  if (euCountries.includes(country)) {
    targetClassNameSet.add('reqinfo--EU');
  }

  clearDynamicDocs();

  // iterate over extra documents, match any subset of target classes.
  document.querySelectorAll(`.ConnectRequiredInfoDoc`).forEach((elem) => {
    const classSet = new Set(
      [...elem.classList].filter((k) => k.startsWith('reqinfo')),
    );

    if (isSuperset(targetClassNameSet, classSet)) {
      elem.style.display = 'block';
    }
  });
};

export const ConnectRequiredInfo = (props: ConnectRequiredInfoProps) => {
  const {account} = useAccount();
  const {api} = useApi();

  // State for cp summary fetch
  const [cpSummary, setCpSummary] = React.useState({
    response: new Set(),
    error: false,
    loading: true,
  });

  // Load static context data
  const {
    capabilityTypesData,
    countryMapData,
    countryTosMapData,
    entityTypeStructureMapData,
  } = props;
  const capabilityTypes = JSON.parse(capabilityTypesData);
  const countryMap = JSON.parse(countryMapData);
  const countryTosMap = JSON.parse(countryTosMapData);
  const entityTypeStructureMap = JSON.parse(entityTypeStructureMapData);

  // Account for an edge case where the `defaultSelection` country is not present in the
  // data returned from the server
  const allCountries = Object.keys(countryMap);
  const selection = allCountries.includes(defaultSelection.country)
    ? defaultSelection
    : {...defaultSelection, ...{country: allCountries[0]}};

  // Defaults for the page
  const [curSelection, setSelection] = useState(selection);

  const activeSections = (reqs: Object): Array<string> => {
    const rawSections = new Set([
      ...Object.keys(reqs)
        .flat()
        .map((key) => key.split('.')[0]),
    ]);

    const sections = ['account']; // always available
    [
      'company',
      'individual',
      'representative',
      'directors',
      'owners',
      'executives',
    ].forEach((searchKey) => {
      if (rawSections.has(searchKey)) {
        sections.push(searchKey);
      }
    });
    return sections;
  };

  React.useEffect(() => {
    const {
      capabilityTypeSet,
      country,
      tosType,
      entityType,
      businessStructure,
    } = curSelection;

    clearDynamicDocs();

    setCpSummary({
      response: {},
      error: false,
      loading: true,
    });

    const payload = {
      capabilityTypes: Array.from(capabilityTypeSet),
      country,
      tosType,
      entityType,
      businessStructure,
    };

    api
      .getRequiredVerificationInformationSummary(payload)
      .then((data) => {
        showDynamicDocs(
          curSelection.country,
          curSelection.entityType,
          activeSections(data),
          [...curSelection.capabilityTypeSet],
        );
        setCpSummary({
          response: data,
          error: false,
          loading: false,
        });
      })
      .catch((err) => {
        setCpSummary({
          response: {},
          error: true,
          loading: false,
        });
      });
  }, [curSelection, api]);

  return (
    <>
      <ConnectRequiredInfoSelector
        capabilityTypes={capabilityTypes}
        countryMap={countryMap}
        countryTosMap={countryTosMap}
        entityTypeStructureMap={entityTypeStructureMap}
        curSelection={curSelection}
        setSelection={setSelection}
      />

      <ConnectRequiredInfoTable
        error={cpSummary.error}
        loading={cpSummary.loading}
        reqData={cpSummary.response}
        curSelection={curSelection}
        isLoggedIn={!!account}
      />
    </>
  );
};
